import { useState } from 'react'
import {
  Card, Col, Descriptions, Empty, Row, Select, Space, Tag, Timeline, Typography,
} from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'

import { workOrdersApi, maintenanceAuditApi } from '@/features/maintenance/api'
import {
  spareProcurementApi,
  type EnrichedLinkRow,
} from '@/features/maintenance/spareProcurementApi'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ProcurementStatusBadge from './components/ProcurementStatusBadge'
import { useMasterLookups } from './components/useMasterLookups'

const { Text } = Typography

/**
 * Phase 3.2-B — Per-WO procurement status drill-down.
 *
 * Spec §4. Picks the WO (or accepts `:wo_id` URL param) and shows:
 *   • Work-order summary
 *   • All procurement links (one row per spare requirement)
 *   • Audit timeline aggregated from each link
 *
 * Every field comes from the backend — no client-side joins.
 */
export default function MaintenanceProcurementStatusPage() {
  const { wo_id: paramWo } = useParams<{ wo_id?: string }>()
  const { plantId } = usePlantId()
  const { machineCode, deptName } = useMasterLookups()
  const [woId, setWoId] = useState<string | undefined>(paramWo)

  const { data: workOrders } = useQuery({
    queryKey: ['wo-list-all', plantId],
    enabled: !!plantId,
    queryFn: () => workOrdersApi.list({
      plant_id: plantId, only_open: false, limit: 500,
    }),
  })
  const wo = (workOrders ?? []).find((w) => w.id === woId)

  const linksQuery = useQuery<EnrichedLinkRow[]>({
    queryKey: ['sp-links-for-wo', plantId, woId],
    enabled: !!plantId && !!woId,
    queryFn: () => spareProcurementApi.listLinks({
      plant_id: plantId, wo_id: woId, limit: 100,
    }),
  })

  // Audit timeline — aggregate the audit rows for every link + the WO
  // itself. Backend exposes per-entity audit; we hit it for the WO and
  // each link.
  const woAudit = useQuery({
    queryKey: ['audit-wo', woId],
    enabled: !!woId,
    queryFn: () => maintenanceAuditApi.forEntity(woId!, 100),
  })

  return (
    <div>
      <PageHeader
        title="Spare Procurement Status"
        subtitle="Where is my spare? Complete trail from PR to payment."
      />

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space>
          <Text>Pick a Work Order:</Text>
          <Select
            showSearch
            placeholder="Select WO"
            style={{ width: 360 }}
            value={woId}
            onChange={setWoId}
            options={(workOrders ?? []).map((w) => ({
              label: `${w.work_order_no} — ${w.priority} ${w.status}`,
              value: w.id,
            }))}
            filterOption={(i, o) =>
              (o?.label as string).toLowerCase().includes(i.toLowerCase())
            }
          />
        </Space>
      </Card>

      {!woId && <Empty description="Select a Work Order to begin" />}

      {wo && (
        <Card title="Work order summary" style={{ marginBottom: 12 }}>
          <Descriptions size="small" bordered column={3}>
            <Descriptions.Item label="WO">{wo.work_order_no}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag>{wo.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Priority">
              <Tag>{wo.priority}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Machine">
              {machineCode(wo.machine_id)}
            </Descriptions.Item>
            <Descriptions.Item label="Department">
              {deptName(wo.department_id)}
            </Descriptions.Item>
            <Descriptions.Item label="Type">{wo.maintenance_type}</Descriptions.Item>
            <Descriptions.Item label="Reported">
              {wo.reported_datetime
                ? dayjs(wo.reported_datetime).format('DD MMM HH:mm') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Downtime">
              {wo.downtime_minutes != null ? `${wo.downtime_minutes} min` : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Action Taken" span={3}>
              {wo.action_taken ?? '—'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {linksQuery.isLoading && <Card><Text>Loading…</Text></Card>}

      {linksQuery.data && linksQuery.data.length === 0 && (
        <Empty description="No spare procurement raised for this WO" />
      )}

      <Row gutter={12}>
        <Col xs={24} lg={14}>
          {(linksQuery.data ?? []).map((link) => (
            <Card
              key={link.link_id}
              size="small"
              style={{ marginBottom: 12 }}
              title={(
                <Space size={6} wrap>
                  <Text strong>{link.spare_code ?? '—'}</Text>
                  <Text type="secondary">{link.spare_name ?? ''}</Text>
                  <ProcurementStatusBadge status={link.status} />
                  {link.direct_charge && <Tag color="purple">DIRECT CHARGE</Tag>}
                </Space>
              )}
            >
              <Descriptions size="small" bordered column={2}>
                <Descriptions.Item label="Required qty">
                  {link.required_qty.toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Available stock">
                  {link.current_stock?.toFixed(2) ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="PR no">
                  {link.pr_no ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="PR status">
                  {link.pr_status ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="PO no">
                  {link.po_no ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="PO status">
                  {link.po_status ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Vendor" span={2}>
                  {link.supplier_name ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="GRN qty accepted">
                  {link.grn_qty_accepted?.toFixed(2) ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Landed rate">
                  {link.grn_landed_rate != null
                    ? `₹ ${link.grn_landed_rate.toFixed(4)} `
                    : '—'}
                  {link.grn_landed_rate != null && <Tag>SYSTEM CALCULATED</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Invoice">
                  {link.invoice_no ?? '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Match status">
                  {link.match_status
                    ? <ProcurementStatusBadge status={link.match_status} />
                    : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Total payable">
                  {link.invoice_total_payable != null
                    ? `₹ ${link.invoice_total_payable.toFixed(2)}` : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Updated">
                  {link.updated_at
                    ? dayjs(link.updated_at).format('DD MMM HH:mm')
                    : link.created_at
                      ? dayjs(link.created_at).format('DD MMM HH:mm')
                      : '—'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          ))}
        </Col>

        <Col xs={24} lg={10}>
          <Card title="Audit timeline" size="small">
            {woAudit.isLoading ? <Text>Loading…</Text>
              : !woAudit.data || woAudit.data.length === 0
                ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No audit rows" />
                : (
                  <Timeline
                    items={woAudit.data.map((row) => {
                      const after = (row.after_data ?? {}) as Record<string, unknown>
                      const isSystem = after._system_generated === true
                      const reason = after._reason as string | undefined
                      const source = after._source_module as string | undefined
                      return {
                        color: isSystem ? 'blue' : 'green',
                        children: (
                          <div>
                            <Space size={4} wrap>
                              <Text strong>
                                {row.action.replace(/_/g, ' ')}
                              </Text>
                              {isSystem && <Tag color="geekblue">SYSTEM</Tag>}
                              {source && <Tag>{source}</Tag>}
                            </Space>
                            <div>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                {dayjs(row.performed_at).format('DD MMM HH:mm')}
                              </Text>
                            </div>
                            {reason && (
                              <div style={{ marginTop: 4 }}>
                                <Text type="secondary">Reason: </Text>{reason}
                              </div>
                            )}
                          </div>
                        ),
                      }
                    })}
                  />
                )}
          </Card>
        </Col>
      </Row>
    </div>
  )
}
